import React, { createContext, useState, useEffect, useContext } from 'react';
import KeycloakService from '../services/KeycloakService';
import UserApiService from '../services/UserApiService';

// Create context
const AuthContext = createContext(null);
const LearningProgressContext = createContext();

/**
 * AuthProvider component to wrap the application with authentication context
 */
export const AuthProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize Keycloak
  useEffect(() => {
    const initKeycloak = async () => {
      console.log('Starting Keycloak initialization in AuthContext');
      try {
        // Initialize Keycloak
        console.log('Calling KeycloakService.init()');
        const isAuthenticated = await KeycloakService.init();
        console.log('KeycloakService.init() returned:', isAuthenticated);
        setAuthenticated(isAuthenticated);
        setInitialized(true);

        // If authenticated, load user profile
        if (isAuthenticated) {
          console.log('User is authenticated, loading profile and roles');
          try {
            // Get user roles
            const roles = KeycloakService.getRoles();
            console.log('User roles:', roles);
            setUserRoles(roles);
            
            // Verify token is available
            const token = KeycloakService.getToken();
            console.log('Token available:', !!token);
            if (!token) {
              console.error('No token available after authentication');
              setError('Authentication error: No token available');
              setAuthenticated(false);
              return;
            }

            // Get user profile from Keycloak
            const profile = await KeycloakService.loadUserProfile();
            console.log('User profile loaded:', profile);
            setUserProfile(profile);

            // Get additional user info from API
            try {
              console.log('Fetching additional user info from API');
              const userInfo = await UserApiService.getUserInfo();
              console.log('Additional user info received:', userInfo);
              // Merge API user info with Keycloak profile
              setUserProfile(prevProfile => {
                const merged = { ...prevProfile, ...userInfo };
                console.log('Merged user profile:', merged);
                return merged;
              });
            } catch (apiError) {
              console.warn('Could not fetch additional user info from API:', apiError);
              // Continue with just the Keycloak profile
            }
            // Log trạng thái cuối cùng để debug
            setTimeout(() => {
              console.log('[AuthContext DEBUG] authenticated:', isAuthenticated);
              console.log('[AuthContext DEBUG] userRoles:', roles);
              console.log('[AuthContext DEBUG] userProfile:', profile);
            }, 1000);
          } catch (profileError) {
            console.error('Error loading user profile:', profileError);
            setError('Failed to load user profile');
          }
        } else {
          console.log('User is not authenticated after initialization');
        }
      } catch (initError) {
        console.error('Error initializing Keycloak:', initError);
        setError('Failed to initialize authentication');
      } finally {
        console.log('Keycloak initialization process completed, setting loading to false');
        setLoading(false);
      }
    };

    initKeycloak();
  }, []);

  // Login function
  const login = (callbackUrl) => {
    return KeycloakService.login(callbackUrl);
  };

  // Logout function
  const logout = () => {
    return KeycloakService.logout();
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return KeycloakService.hasRole(role);
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return KeycloakService.hasAnyRole(roles);
  };

  // Check if user has all of the specified roles
  const hasAllRoles = (roles) => {
    return KeycloakService.hasAllRoles(roles);
  };

  // Context value
  const value = {
    initialized,
    authenticated,
    userProfile,
    userRoles,
    loading,
    error,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use the auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const LearningProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState({ totalLessons: 0, completedLessons: 0 });
  return (
    <LearningProgressContext.Provider value={{ progress, setProgress }}>
      {children}
    </LearningProgressContext.Provider>
  );
};

export const useLearningProgress = () => useContext(LearningProgressContext);

export default AuthContext;